import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, AlertOctagon, PackageX, Truck, Building2, ChevronRight, Layers, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { KPI, SectionCard } from "./AdminJobCards";

const inr = (v) => {
  const n = Number(v || 0);
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
};

export default function AdminInventory() {
  const [summary, setSummary] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [s, l] = await Promise.all([
        api.get("/admin/ops/inventory/summary").then((r) => r.data).catch(() => null),
        api.get("/admin/ops/inventory/low-stock", { params: { limit: 100 } }).then((r) => r.data).catch(() => ({ rows: [] })),
      ]);
      setSummary(s);
      setLowStock(l.rows || []);
      setLoading(false);
    })();
  }, []);

  const kpis = summary?.kpis || {};
  const topGarages = summary?.top_garages || [];
  const topVendors = summary?.top_vendors || [];

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Platform Ops</div>
        <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Inventory Overview</h1>
        <p className="text-[13px] text-[#667085] mt-0.5">Cross-garage inventory health — spot low-stock spikes and top vendors at a glance.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPI testId="inv-kpi-skus" label="Total SKUs" value={kpis.total_skus ?? "—"} Icon={Package} sub="Active parts" />
        <KPI testId="inv-kpi-low" label="Low Stock" value={kpis.low_stock ?? "—"} Icon={AlertOctagon} tone="orange" sub="At or below reorder" />
        <KPI testId="inv-kpi-out" label="Out of Stock" value={kpis.out_of_stock ?? "—"} Icon={PackageX} tone="red" />
        <KPI testId="inv-kpi-vendors" label="Vendors" value={kpis.total_vendors ?? "—"} Icon={Truck} tone="blue" />
        <KPI testId="inv-kpi-value" label="Stock Value" value={inr(kpis.stock_value ?? 0)} Icon={Layers} tone="honeydew" />
        <KPI testId="inv-kpi-mvmt" label="Movements 7d" value={kpis.movements_7d ?? "—"} Icon={TrendingUp} tone="green" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SectionCard
            title="At-risk parts"
            subtitle={loading ? "Loading…" : `${lowStock.length} SKU(s) needing attention`}
            testId="inv-low-stock-card"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-[12px]">
                <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Part</th>
                    <th className="px-4 py-2 font-semibold">Garage</th>
                    <th className="px-4 py-2 font-semibold">Stock</th>
                    <th className="px-4 py-2 font-semibold">Reserved</th>
                    <th className="px-4 py-2 font-semibold">Reorder At</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1FAEE]">
                  {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#667085]">Loading…</td></tr>}
                  {!loading && lowStock.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-[#12B76A]">All good — no low or out-of-stock parts.</td></tr>
                  )}
                  {!loading && lowStock.map((r) => (
                    <tr key={r.id} data-testid={`inv-row-${r.id}`} className={`${r.state === "out_of_stock" ? "bg-[#e63946]/[0.03]" : ""} hover:bg-[#F7F9FC]`}>
                      <td className="px-4 py-2">
                        <div className="text-[13px] font-semibold text-[#1d3557]">{r.name}</div>
                        <div className="text-[11px] text-[#667085] font-mono">{r.sku} · {r.brand || "—"}</div>
                      </td>
                      <td className="px-4 py-2">
                        <Link to={`/admin/garages/${r.garage_id}`} className="text-[#457b9d] hover:underline">{r.garage_name}</Link>
                      </td>
                      <td className="px-4 py-2 font-mono font-bold text-[#1d3557]">{r.stock_qty}</td>
                      <td className="px-4 py-2 font-mono text-[#667085]">{r.reserved_qty || 0}</td>
                      <td className="px-4 py-2 font-mono text-[#667085]">{r.reorder_level || "—"}</td>
                      <td className="px-4 py-2">
                        {r.state === "out_of_stock"
                          ? <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold bg-[#e63946]/10 text-[#e63946] border-[#e63946]/25">Out of stock</span>
                          : <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold bg-[#F79009]/10 text-[#B54708] border-[#F79009]/25">Low stock</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard title="Top Garages by SKU Count" testId="inv-top-garages">
            <div className="divide-y divide-[#F1FAEE]">
              {topGarages.length === 0 && <div className="p-6 text-center text-[13px] text-[#667085]">No data</div>}
              {topGarages.map((g, i) => (
                <Link key={g.garage_id} to={`/admin/garages/${g.garage_id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-[#F7F9FC]">
                  <div className="w-7 h-7 rounded-md grid place-items-center bg-[#F1FAEE] text-[#457b9d]">
                    <Building2 size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#1d3557] truncate">#{i + 1} {g.garage_name}</div>
                    <div className="text-[11px] text-[#667085] truncate">{g.city || "—"}</div>
                  </div>
                  <div className="text-[13px] font-mono font-bold text-[#1d3557]">{g.sku_count}</div>
                  <ChevronRight size={13} className="text-[#a8dadc]" />
                </Link>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Top Vendors" testId="inv-top-vendors">
            <div className="divide-y divide-[#F1FAEE]">
              {topVendors.length === 0 && <div className="p-6 text-center text-[13px] text-[#667085]">No data</div>}
              {topVendors.map((v, i) => (
                <div key={v.vendor_id || i} className="flex items-center gap-3 px-5 py-3">
                  <div className="w-7 h-7 rounded-md grid place-items-center bg-[#F1FAEE] text-[#457b9d]">
                    <Truck size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#1d3557] truncate">#{i + 1} {v.vendor_name}</div>
                    <div className="text-[11px] text-[#667085]">Preferred vendor for {v.parts_count} SKU(s)</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
